import { signal, WritableSignal } from '@angular/core';
import { FormArray, Validators } from '@angular/forms';
import { StudyPaymentApprovalThresholdEntry } from '@app/generated-api';
import { PersistentFormControl, PersistentFormGroup } from '@utility/persistent-forms';
import { manualValidator, moreThanTodayDateValidator, ValidatorHandle } from '@utility/utility.validators';

export class ThresholdRowFormGroup extends PersistentFormGroup<{
    threshold1: PersistentFormControl<number | null>;
    threshold2: PersistentFormControl<number | null>;
    effectiveDate: PersistentFormControl<string | null>;
    modifiedByFullName: PersistentFormControl<string | null>;
    modifiedTimestamp: PersistentFormControl<string | null>;
}> {
    readonly isDirty: WritableSignal<boolean> = signal(false);

    constructor(row?: StudyPaymentApprovalThresholdEntry, dirty = false) {
        super({
            threshold1: new PersistentFormControl<number | null>(null),
            threshold2: new PersistentFormControl<number | null>(null),
            effectiveDate: new PersistentFormControl<string | null>(null),
            modifiedByFullName: new PersistentFormControl<string | null>(null),
            modifiedTimestamp: new PersistentFormControl<string | null>(null),
        });
        if (row) {
            this.patchValue({
                threshold1: row.threshold1 ?? null,
                threshold2: row.threshold2 ?? null,
                effectiveDate: row.effectiveDate ?? null,
                modifiedByFullName: row.modifiedByFullName ?? null,
                modifiedTimestamp: row.modifiedTimestamp ?? null,
            });
        }
        this.isDirty.set(dirty);
    }
}

export class StudyPaymentApprovalThresholdFormGroup extends PersistentFormGroup<{
    threshold1: PersistentFormControl<string | null>;
    threshold2: PersistentFormControl<string | null>;
    effectiveDate: PersistentFormControl<Date | null>;
    timeline: FormArray<ThresholdRowFormGroup>;
}> {
    readonly timelineFormGroups: WritableSignal<ThresholdRowFormGroup[]> = signal([]);

    constructor() {
        const threshold1Handle = new ValidatorHandle();
        const threshold2Handle = new ValidatorHandle();

        super({
            threshold1: new PersistentFormControl<string | null>(null, [
                Validators.min(0.01),
                Validators.max(1e9),
                manualValidator(threshold1Handle, {
                    threshold1GreaterThanThreshold2: 'Threshold 1 must be less than Threshold 2',
                }),
            ]),
            threshold2: new PersistentFormControl<string | null>(null, [
                Validators.min(0.01),
                Validators.max(1e9),
                manualValidator(threshold2Handle, {
                    threshold1GreaterThanThreshold2: 'Threshold 1 must be less than Threshold 2',
                }),
            ]),
            // initialize date with tomorrow
            effectiveDate: new PersistentFormControl<Date | null>(null, [
                Validators.required,
                moreThanTodayDateValidator,
            ]),
            timeline: new FormArray<ThresholdRowFormGroup>([]),
        });

        threshold1Handle.formControl = this.controls.threshold1;
        threshold2Handle.formControl = this.controls.threshold2;

        this.addValidators((control) => {
            const formGroup = control as StudyPaymentApprovalThresholdFormGroup;
            const value = formGroup.getRawValue();

            if (!formGroup.value) return null;

            const t1 = value.threshold1 ? parseFloat(value.threshold1) : null;
            const t2 = value.threshold2 ? parseFloat(value.threshold2) : null;

            const empty = t1 === null || t2 === null;
            const valid = t1! < t2!;
            threshold1Handle.setValid(empty || valid);
            threshold2Handle.setValid(empty || valid);

            return null;
        });

        this.patchValue({
            effectiveDate: new Date(new Date().setDate(new Date().getDate() + 1)), // set to tomorrow
        });
    }
}
